/* eslint-disable react/no-unstable-nested-components */
import {
    ActionButton,
    Button,
    Card,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import {
    ColumnDef,
    getCoreRowModel,
    Row,
    useReactTable,
    VisibilityState,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    APP_COLORS,
    CPT_CODES,
    CPT_CODES_DESCRIPTION,
    LISTS_DEFAULT_LIMIT,
} from "../../../../utils/constants";
import { useDebounce } from "../../../../hooks/helpers";
import {
    cn,
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
    truncateString,
} from "../../../../utils/helpers";
import {
    useExportAgingReport,
    useGetAgingReport,
} from "../../hooks/queries/reports.queries";
import { SessionPlaceOfServiceCode } from "../../../../utils/types/session";
import { IAppCustomDates } from "../../../../utils/types";
import { BillStatus } from "../../../billing/types/billing.types";
import { ClaimStatus } from "../../../claims/types/claims.types";
import { useFetchInsuranceProviders } from "../../../clients/hooks/clients.queries";
import { useGetRemarkCodes } from "../../../remark-codes/hooks/remark-code.queries";
import Loader from "../../../../shared-ui/Loader/Loader";
import { useFetchClientList } from "../../../../hooks/queries/client";
import { useFetchProviders } from "../../../providers/hooks/providers.queries";
import ListState from "../../../../components/ListState/ListState";
import { IAgingReport } from "../../types/reports.types";
import {
    AGING_AGE_FILTERS,
    getAgingReportDateFilters,
} from "../../helpers/reports.helper";
import InfoIcon from "../../../../icons/Info";
import {
    getBillStatusFilterLabel,
    getBillStatusTag,
} from "../../../billing/helpers/billing.helpers";
import {
    getClaimStatusFilterLabel,
    getStatusTag,
} from "../../../claims/helpers/claims.helper";
import useToast from "../../../../hooks/useToast";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import InsuranceProviderFilter from "../../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";
import Navbar from "../../../../components/Navbar/Navbar";

export default function AgingReportList() {
    const claimsColumnsVisibilityLs = localStorage.getItem(
        "claims_columns_visibility"
    );

    const [searchParams, setSearchParams] = useSearchParams();
    const [clientSearchString, setClientSearchString] = useState("");
    const [providerSearchString, setProviderSearchString] = useState("");
    const [columnVisibility, setColumnVisibility] = useState<
        Record<string, boolean>
    >(() =>
        claimsColumnsVisibilityLs ? JSON.parse(claimsColumnsVisibilityLs) : {}
    );

    const searchStringFilter = searchParams.get("search_string") || "";
    const clientNamesFilter = searchParams.getAll("client_names[]");
    const providersFilter = searchParams.getAll("providers[]");
    const customDateFilter = searchParams.get("custom_date") || "this_month";
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const cptCodesFilter = searchParams.getAll("cpt_codes[]");
    const posCodesFilter = searchParams.getAll("pos_codes[]");
    const claimStatusesFilter = searchParams.getAll("claim_statuses[]");
    const billStatusesFilter = searchParams.getAll("bill_statuses[]");
    const insuranceProviderIdsFilter = searchParams.getAll(
        "insurance_provider_ids[]"
    );
    const remarkCodesFilter = searchParams.getAll("remark_codes[]");
    const excludeZeroBalanceFilter =
        searchParams.get("exclude_zero_balance") || "";
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const [isExporting, setIsExporting] = useState(false);

    const [sortConfig, setSortConfig] = useState<{
        key: string;
        direction: string;
    }>({
        key: "",
        direction: "",
    });

    const sortAttributeFilter =
        searchParams.get("sort_attribute") || sortConfig.key;
    const sortOrderFilter =
        searchParams.get("sort_order") || sortConfig.direction;

    const sortableColumns = [
        "date_of_service",
        "client_first_name",
        "client_last_name",
        "provider_first_name",
        "provider_last_name",
        "insurance_member_id",
        "date_of_birth",
        "claim_external_reference_id",
        "claim_balance",
        "claim_billed_amount",
        "patient_charged_amount",
        "client_paid_amount",
        "insurance_paid_amount",
        "patient_writeoff_amount",
        "insurance_writeoff_amount",
        "total_writeoff_amount",
        "pr100_amount",
        "pr100_collected",
        "provider_npi",
    ];

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);
    const agingDateFilters = getAgingReportDateFilters(
        customDateFilter,
        fromDateFilter,
        toDateFilter
    );

    const debouncedSearchStringFilter = useDebounce(searchStringFilter);
    const debouncedClientSearchString = useDebounce(clientSearchString);
    const debouncedProviderSearchString = useDebounce(providerSearchString);

    const insuranceProviders = useFetchInsuranceProviders();
    const remarkCodes = useGetRemarkCodes();

    const { toast } = useToast();

    const navigate = useNavigate();

    const {
        data: clients,
        isLoading: clientListLoading,
        error: clientListError,
    } = useFetchClientList({
        page: 1,
        search_string: debouncedClientSearchString,
    });

    const {
        data: providers,
        isLoading: providerListLoading,
        error: providerListError,
    } = useFetchProviders({
        page: 1,
        search_string: debouncedProviderSearchString,
        limit: 100,
    });

    const { data, isLoading, error, isSuccess } = useGetAgingReport({
        provider_ids: providersFilter,
        client_names: clientNamesFilter,
        from_date: agingDateFilters.from_date,
        search_string: debouncedSearchStringFilter,
        to_date: agingDateFilters.to_date,
        page: pageFilter,
        limit: limitFilter,
        sort_attribute: sortAttributeFilter,
        sort_order: sortOrderFilter,
        custom_date: agingDateFilters.custom_date,
        cpt_codes: cptCodesFilter,
        pos_codes: posCodesFilter,
        claim_statuses: claimStatusesFilter,
        bill_statuses: billStatusesFilter,
        insurance_provider_ids: insuranceProviderIdsFilter,
        remark_codes: remarkCodesFilter,
        exclude_zero_balance: excludeZeroBalanceFilter,
    });

    const handleRowClick = (row: Row<IAgingReport>) => {
        navigate(
            `/billing-and-claims/${row.original.client_id}/${
                row.original.note_id
            }${
                row.original.claim_id
                    ? `?active_claim_id=${row.original.claim_id}`
                    : ""
            }`
        );
    };

    const handleSetProviderNamesFilter = (providerName: string) => {
        if (providersFilter.includes(providerName)) {
            searchParams.delete("providers[]", providerName);
        } else {
            searchParams.append("providers[]", providerName);
        }
        searchParams.set("page", "1");
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetClientNamesFilter = (clientName: string) => {
        if (clientNamesFilter.includes(clientName)) {
            searchParams.delete("client_names[]", clientName);
        } else {
            searchParams.append("client_names[]", clientName);
        }
        searchParams.set("page", "1");
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetFromDateFilter = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetToDateFilter = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetPageFilter = (page: number) => {
        searchParams.set("page", String(page));
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetLimitFilter = (limit: number) => {
        searchParams.set("limit", String(limit));
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetSearchStringFilter = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (e.target.value === "") searchParams.delete("search_string");
        else searchParams.set("search_string", e.target.value);
        searchParams.set("page", "1");
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetCustomDateFilter = (customDate: string) => {
        if (customDate === "") searchParams.delete("custom_date");
        else searchParams.set("custom_date", customDate);
        if (AGING_AGE_FILTERS.has(customDate)) {
            searchParams.delete("from_date");
            searchParams.delete("to_date");
        }
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetCptCodesFilter = (cptCode: string) => {
        if (cptCodesFilter.includes(cptCode)) {
            searchParams.delete("cpt_codes[]", cptCode);
        } else {
            searchParams.append("cpt_codes[]", cptCode);
        }
        searchParams.set("page", "1");
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetPosCodesFilter = (posCode: string) => {
        if (posCodesFilter.includes(posCode)) {
            searchParams.delete("pos_codes[]", posCode);
        } else {
            searchParams.append("pos_codes[]", posCode);
        }
        searchParams.set("page", "1");
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetClaimStatusesFilter = (claimStatus: string) => {
        if (claimStatusesFilter.includes(claimStatus)) {
            searchParams.delete("claim_statuses[]", claimStatus);
        } else {
            searchParams.append("claim_statuses[]", claimStatus);
        }
        searchParams.set("page", "1");
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetBillStatusesFilter = (billStatus: string) => {
        if (billStatusesFilter.includes(billStatus)) {
            searchParams.delete("bill_statuses[]", billStatus);
        } else {
            searchParams.append("bill_statuses[]", billStatus);
        }
        searchParams.set("page", "1");
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleInsuranceProviderIdsChange = (
        insuranceProviderIds: string[]
    ) => {
        searchParams.delete("insurance_provider_ids[]");
        insuranceProviderIds.forEach((insuranceProviderId) =>
            searchParams.append("insurance_provider_ids[]", insuranceProviderId)
        );
        searchParams.set("page", "1");
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetRemarkCodesFilter = (remarkCode: string) => {
        if (remarkCodesFilter.includes(remarkCode)) {
            searchParams.delete("remark_codes[]", remarkCode);
        } else {
            searchParams.append("remark_codes[]", remarkCode);
        }
        searchParams.set("page", "1");
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetExcludeZeroBalanceFilter = (value: string) => {
        if (value === "") searchParams.delete("exclude_zero_balance");
        else searchParams.set("exclude_zero_balance", value);
        searchParams.set("page", "1");
        localStorage.setItem("agingreports_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const insuranceProviderOptions = [
        { label: "Self-pay", value: "self_pay" },
        ...(insuranceProviders.data?.data?.map((provider) => ({
            label: provider.alias || provider.name,
            value: provider.insurance_provider_id,
        })) || []),
    ].sort((a, b) => a.label.localeCompare(b.label));

    const filteredInsuranceProviderNames = insuranceProviderOptions
        .filter((option) => insuranceProviderIdsFilter.includes(option.value))
        .map((option) => option.label);

    const cptCodeSectionDisplay = (row: Row<IAgingReport>) => {
        if (row.original.cpt_code) {
            return (
                <ActionButton
                    variant="normal"
                    size="auto"
                    iconRight={<InfoIcon />}
                    actionTitle={
                        CPT_CODES_DESCRIPTION[
                            row.original.cpt_code as string
                        ] || ""
                    }
                >
                    <span>{removeEnumUnderscore(row.original.cpt_code)}</span>
                </ActionButton>
            );
        }

        return <span>--</span>;
    };

    const handleColumnVisibilityChange = (
        updaterOrValue:
            | VisibilityState
            | ((old: VisibilityState) => VisibilityState)
    ) => {
        setColumnVisibility((prevState) => {
            let newState: VisibilityState;

            if (typeof updaterOrValue === "function") {
                newState = updaterOrValue(prevState);
            } else {
                newState = updaterOrValue;
            }

            localStorage.setItem(
                "claims_columns_visibility",
                JSON.stringify(newState)
            );

            return newState;
        });
    };

    const columns: ColumnDef<IAgingReport>[] = useMemo(() => {
        return [
            {
                accessorKey: "client_first_name",
                header: "Client First Name",
                cell: ({ row }) => (
                    <div className="flex flex-col gap-y-2">
                        {row.original.client_first_name}
                        <span className="flex items-center gap-x-0.5">
                            <span className="text-textSec">{`Cpt: `}</span>
                            {cptCodeSectionDisplay(row)}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: "client_last_name",
                header: "Client Last Name",
                cell: ({ row }) => (
                    <div className="flex flex-col gap-y-2">
                        {row.original.client_last_name}
                    </div>
                ),
            },
            {
                accessorKey: "date_of_birth",
                header: "Date of Birth",
                cell: ({ row }) => (
                    <span>
                        {row.original.date_of_birth
                            ? formatDate(
                                  `${row.original.date_of_birth}T00:00:00`,
                                  true
                              )
                            : "--"}
                    </span>
                ),
            },
            {
                accessorKey: "date_of_service",
                header: "DATE OF SERVICE",
                cell: ({ row }) => (
                    <div className="flex flex-col gap-y-2">
                        {formatDate(row.original.date_of_service)}
                        <span className="flex items-center gap-x-0.5">
                            <span className="text-textSec">{`Bill: `}</span>
                            {getBillStatusTag(
                                row.original.bill_status as BillStatus
                            )}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: "claim_external_reference_id",
                header: "MANTLE CLAIM ID",
                cell: ({ row }) => (
                    <div className="flex flex-col gap-y-2">
                        {row.original.claim_external_reference_id ?? "--"}
                        <span className="flex items-center gap-x-0.5">
                            <span className="text-textSec">{`Claim: `}</span>
                            {row.original.claim_status
                                ? getStatusTag(
                                      row.original.claim_status as ClaimStatus
                                  )
                                : "--"}
                        </span>
                    </div>
                ),
            },
            {
                accessorKey: "client_chart_number",
                header: "Chart Number",
                cell: ({ row }) => {
                    const poscodeToDisplay = () => {
                        const regex = /^(\d+) - (.+)$/;

                        if (row.original.pos_code) {
                            return (
                                <ActionButton
                                    variant="normal"
                                    size="auto"
                                    iconRight={<InfoIcon />}
                                    actionTitle={
                                        row.original.pos_code.match(
                                            regex
                                        )?.[2] || ""
                                    }
                                >
                                    {row.original.pos_code.match(regex)?.[1] ||
                                        ""}
                                </ActionButton>
                            );
                        }
                        return <span>--</span>;
                    };

                    return (
                        <div className="flex flex-col gap-y-2">
                            {row.original.client_chart_number ?? "--"}
                            <span className="flex items-center gap-x-0.5">
                                <span className="text-textSec">{`POS: `}</span>
                                {poscodeToDisplay()}
                            </span>
                        </div>
                    );
                },
            },
            {
                accessorKey: "provider_first_name",
                header: "Provider First Name",
                cell: ({ row }) => (
                    <span>{row.original.provider_first_name}</span>
                ),
            },
            {
                accessorKey: "provider_last_name",
                header: "Provider Last Name",
                cell: ({ row }) => (
                    <span>{row.original.provider_last_name}</span>
                ),
            },
            {
                accessorKey: "provider_npi",
                header: "Provider NPI",
                cell: ({ row }) => (
                    <span>{row.original.provider_npi ?? "--"}</span>
                ),
            },
            {
                accessorKey: "modifier_codes",
                header: "Modifier Codes",
                cell: ({ row }) => (
                    <span>{row.original.modifier_codes || "--"}</span>
                ),
            },
            {
                accessorKey: "insurance_provider_name",
                header: "INSURANCE PROVIDER",
                cell: ({ row }) => (
                    <span>
                        {row.original.insurance_provider_name?.toUpperCase() ??
                            "--"}
                    </span>
                ),
            },
            {
                accessorKey: "insurance_member_id",
                header: "INSURANCE Member ID",
                cell: ({ row }) => (
                    <span>{row.original.insurance_member_id ?? "--"}</span>
                ),
            },
            {
                accessorKey: "claim_billed_amount",
                header: "Claim billed amount",
                cell: ({ row }) => (
                    <span>
                        {showMoneyInAppFormat(row.original.claim_billed_amount)}
                    </span>
                ),
            },
            {
                accessorKey: "patient_charged_amount",
                header: "Patient charged amount",
                cell: ({ row }) => (
                    <span>
                        {showMoneyInAppFormat(
                            row.original.patient_charged_amount
                        )}
                    </span>
                ),
            },
            {
                accessorKey: "client_paid_amount",
                header: "Client paid amount",
                cell: ({ row }) => (
                    <span>
                        {showMoneyInAppFormat(row.original.client_paid_amount)}
                    </span>
                ),
            },
            {
                accessorKey: "insurance_paid_amount",
                header: "Insurance paid amount",
                cell: ({ row }) => (
                    <span>
                        {showMoneyInAppFormat(
                            row.original.insurance_paid_amount
                        )}
                    </span>
                ),
            },
            {
                accessorKey: "total_writeoff_amount",
                header: "Total writeoff amount",
                cell: ({ row }) => (
                    <span>
                        {showMoneyInAppFormat(
                            row.original.total_writeoff_amount
                        )}
                    </span>
                ),
            },
            {
                accessorKey: "claim_balance",
                header: "Claim balance",
                cell: ({ row }) => {
                    const claimBalance = row.original.claim_balance;

                    if (typeof claimBalance !== "number") {
                        return <span className="font-semibold">--</span>;
                    }

                    return (
                        <span
                            className={cn("font-semibold", {
                                "text-danger": claimBalance < 0,
                            })}
                        >
                            {claimBalance < 0
                                ? `(${showMoneyInAppFormat(
                                      Math.abs(claimBalance)
                                  )})`
                                : showMoneyInAppFormat(claimBalance)}
                        </span>
                    );
                },
            },
            {
                accessorKey: "remark_codes",
                header: "Remark codes",
                cell: ({ row }) => (
                    <span>{row.original.remark_codes ?? "--"}</span>
                ),
            },

            {
                accessorKey: "pr100_amount",
                header: "PR100 amount",
                cell: ({ row }) => (
                    <span>
                        {showMoneyInAppFormat(row.original.pr100_amount)}
                    </span>
                ),
            },
            {
                accessorKey: "pr100_collected",
                header: "PR100 collected",
                cell: ({ row }) => (
                    <span>
                        {showMoneyInAppFormat(row.original.pr100_collected)}
                    </span>
                ),
            },
        ];
    }, []);

    const table = useReactTable({
        columns,
        data: data?.data || [],
        state: {
            columnVisibility,
        },
        getCoreRowModel: getCoreRowModel(),
        onColumnVisibilityChange: handleColumnVisibilityChange,
    });

    const selectedColumnsForExport = table
        .getAllLeafColumns()
        .filter((column) => column.getIsVisible())
        .map(
            (column) =>
                column?.columnDef?.header?.toString()?.toUpperCase() ||
                column.id?.replaceAll("_", " ")?.toUpperCase()
        );

    const exportQuery = useExportAgingReport(
        {
            provider_ids: providersFilter,
            client_names: clientNamesFilter,
            from_date: agingDateFilters.from_date,
            search_string: debouncedSearchStringFilter,
            to_date: agingDateFilters.to_date,
            page: pageFilter,
            limit: limitFilter,
            sort_attribute: sortAttributeFilter,
            sort_order: sortOrderFilter,
            custom_date: agingDateFilters.custom_date,
            cpt_codes: cptCodesFilter,
            pos_codes: posCodesFilter,
            claim_statuses: claimStatusesFilter,
            bill_statuses: billStatusesFilter,
            insurance_provider_ids: insuranceProviderIdsFilter,
            remark_codes: remarkCodesFilter,
            exclude_zero_balance: excludeZeroBalanceFilter,
            selected_columns:
                selectedColumnsForExport && selectedColumnsForExport.length > 0
                    ? selectedColumnsForExport.map((col) =>
                          col === "INSURANCE MEMBER ID" ? "INSURANCE ID" : col
                      )
                    : [],
        },
        isExporting
    );

    const listLengthIsMoreThanExportLimit = Boolean(
        data?.total_count && data?.total_count > 30000
    );

    const handleExportClick = async () => {
        setIsExporting(true);
        await exportQuery.refetch();
        setIsExporting(false);
        if (exportQuery.error) {
            toast({
                mode: "error",
                message: "Could not export aging reports list",
            });
        }
    };

    let agingReportCount;
    if (error) agingReportCount = "";
    else if (isLoading) agingReportCount = "";
    else if (data?.data && data?.data.length === 0) agingReportCount = 0;
    else if (data?.data && data?.data.length > 0)
        agingReportCount = data.total_count;

    return (
        <>
            <Navbar title={`Aging Report(${agingReportCount})`} />
            <div className="page">
                <div>
                    <Card classNames="mb-[--spacing-header]">
                        <div className="flex flex-col gap-y-[--spacing-items]">
                            <SearchInput
                                placeholder="Search by client name, chart number"
                                containerClass="w-[400px]"
                                onChange={handleSetSearchStringFilter}
                                defaultValue={searchStringFilter}
                            />
                            <div className="flex items-center gap-[--spacing-items] justify-between flex-wrap">
                                <div className="flex gap-[--spacing-items] flex-wrap">
                                    <Dropdown>
                                        <DropdownTrigger asChild>
                                            <FilterButton>
                                                Providers
                                            </FilterButton>
                                        </DropdownTrigger>
                                        <DropdownContent
                                            align="end"
                                            width="250px"
                                            collisionPadding={10}
                                        >
                                            <SearchInput
                                                placeholder="Search by provider name"
                                                containerClass="shrink-0 mb-1"
                                                onKeyDown={(e) => {
                                                    if (e.key !== "Escape") {
                                                        e.stopPropagation();
                                                    }
                                                }}
                                                onChange={(e) =>
                                                    setProviderSearchString(
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            {providerListLoading && <Loader />}
                                            {providerListError && (
                                                <ListState
                                                    isError
                                                    context="general"
                                                    stateHelperText="Please try again later"
                                                    errorMsg={
                                                        providerListError
                                                            ?.response?.data
                                                            ?.message ||
                                                        "Error loading clinicians"
                                                    }
                                                    cardClass="h-[150px]"
                                                />
                                            )}
                                            {providers?.data?.map(
                                                (provider) => (
                                                    <DropdownItem
                                                        key={
                                                            provider.provider_id
                                                        }
                                                        onClick={() =>
                                                            handleSetProviderNamesFilter(
                                                                provider.provider_id
                                                            )
                                                        }
                                                        isMulti
                                                        itemValue={
                                                            provider.provider_id
                                                        }
                                                        values={providersFilter}
                                                    >
                                                        {displayNameInRightFormat(
                                                            provider.provider_name
                                                        )}
                                                    </DropdownItem>
                                                )
                                            )}
                                        </DropdownContent>
                                    </Dropdown>
                                    <Dropdown>
                                        <DropdownTrigger asChild>
                                            <FilterButton>Clients</FilterButton>
                                        </DropdownTrigger>
                                        <DropdownContent
                                            width="270px"
                                            collisionPadding={10}
                                        >
                                            <SearchInput
                                                placeholder="Search by client name"
                                                containerClass="shrink-0 mb-1"
                                                onKeyDown={(e) => {
                                                    if (e.key !== "Escape") {
                                                        e.stopPropagation();
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    setClientSearchString(
                                                        e.target.value
                                                    );
                                                }}
                                            />
                                            {clientListLoading && <Loader />}
                                            {error && (
                                                <ListState
                                                    isError
                                                    context="general"
                                                    stateHelperText="Please try again later"
                                                    errorMsg={
                                                        clientListError
                                                            ?.response?.data
                                                            ?.message ||
                                                        "Error loading clients"
                                                    }
                                                    cardClass="h-[150px]"
                                                />
                                            )}
                                            {clients?.data?.map((client) => (
                                                <DropdownItem
                                                    key={client.client_id}
                                                    onClick={() =>
                                                        handleSetClientNamesFilter(
                                                            `${client.first_name} ${client.last_name}`
                                                        )
                                                    }
                                                    isMulti
                                                    itemValue={`${client.first_name} ${client.last_name}`}
                                                    values={clientNamesFilter}
                                                >
                                                    {displayNameInRightFormat({
                                                        firstName:
                                                            client.first_name,
                                                        lastName:
                                                            client.last_name,
                                                    })}
                                                </DropdownItem>
                                            ))}
                                        </DropdownContent>
                                    </Dropdown>
                                    <Dropdown>
                                        <DropdownTrigger asChild>
                                            <FilterButton className="normal-case">
                                                CPT code
                                            </FilterButton>
                                        </DropdownTrigger>
                                        <DropdownContent maxHeight={216}>
                                            {CPT_CODES.map((code) => (
                                                <DropdownItem
                                                    key={code}
                                                    onClick={() =>
                                                        handleSetCptCodesFilter(
                                                            code
                                                        )
                                                    }
                                                    isMulti
                                                    itemValue={code}
                                                    values={cptCodesFilter}
                                                >
                                                    {code}
                                                </DropdownItem>
                                            ))}
                                        </DropdownContent>
                                    </Dropdown>
                                    <Dropdown>
                                        <DropdownTrigger asChild>
                                            <FilterButton className="normal-case">
                                                POS code
                                            </FilterButton>
                                        </DropdownTrigger>
                                        <DropdownContent
                                            maxHeight={216}
                                            width={200}
                                        >
                                            {Object.values(
                                                SessionPlaceOfServiceCode
                                            ).map((code) => (
                                                <DropdownItem
                                                    key={code}
                                                    onClick={() =>
                                                        handleSetPosCodesFilter(
                                                            code
                                                        )
                                                    }
                                                    isMulti
                                                    itemValue={code}
                                                    values={posCodesFilter}
                                                >
                                                    {code}
                                                </DropdownItem>
                                            ))}
                                        </DropdownContent>
                                    </Dropdown>
                                    <InsuranceProviderFilter
                                        values={insuranceProviderIdsFilter}
                                        onChange={
                                            handleInsuranceProviderIdsChange
                                        }
                                        alwaysShowSelfPay
                                    />
                                    <Dropdown>
                                        <DropdownTrigger
                                            asChild
                                            disabled={
                                                Boolean(remarkCodes.error) ||
                                                remarkCodes.isLoading
                                            }
                                        >
                                            <FilterButton>
                                                {remarkCodes.isLoading &&
                                                    "Loading.."}
                                                {remarkCodes.error &&
                                                    "Error loading remark codes"}
                                                {remarkCodes?.data?.data &&
                                                    "Remark Codes"}
                                            </FilterButton>
                                        </DropdownTrigger>
                                        <DropdownContent maxHeight={216}>
                                            {remarkCodes.data?.data?.map(
                                                (code) => (
                                                    <DropdownItem
                                                        key={
                                                            code.remark_code_id
                                                        }
                                                        onClick={() =>
                                                            handleSetRemarkCodesFilter(
                                                                code.code
                                                            )
                                                        }
                                                        isMulti
                                                        itemValue={code.code}
                                                        values={
                                                            remarkCodesFilter
                                                        }
                                                        className="*:!uppercase"
                                                    >
                                                        {code.code}
                                                    </DropdownItem>
                                                )
                                            )}
                                        </DropdownContent>
                                    </Dropdown>
                                    <Dropdown>
                                        <DropdownTrigger asChild>
                                            <FilterButton>
                                                Bill Status
                                            </FilterButton>
                                        </DropdownTrigger>
                                        <DropdownContent
                                            align="end"
                                            width="auto"
                                        >
                                            {Object.values(BillStatus).map(
                                                (status) => (
                                                    <DropdownItem
                                                        key={status}
                                                        onClick={() =>
                                                            handleSetBillStatusesFilter(
                                                                status
                                                            )
                                                        }
                                                        isMulti
                                                        itemValue={status}
                                                        values={
                                                            billStatusesFilter
                                                        }
                                                    >
                                                        {getBillStatusFilterLabel(
                                                            status
                                                        )}
                                                    </DropdownItem>
                                                )
                                            )}
                                        </DropdownContent>
                                    </Dropdown>
                                    <Dropdown>
                                        <DropdownTrigger asChild>
                                            <FilterButton>
                                                Claim Status
                                            </FilterButton>
                                        </DropdownTrigger>
                                        <DropdownContent
                                            align="end"
                                            width="auto"
                                        >
                                            {Object.values(ClaimStatus).map(
                                                (status) => (
                                                    <DropdownItem
                                                        key={status}
                                                        onClick={() =>
                                                            handleSetClaimStatusesFilter(
                                                                status
                                                            )
                                                        }
                                                        isMulti
                                                        itemValue={status}
                                                        values={
                                                            claimStatusesFilter
                                                        }
                                                    >
                                                        {getClaimStatusFilterLabel(
                                                            status
                                                        )}
                                                    </DropdownItem>
                                                )
                                            )}
                                        </DropdownContent>
                                    </Dropdown>
                                    <Dropdown>
                                        <DropdownTrigger asChild>
                                            <FilterButton>
                                                {excludeZeroBalanceFilter ===
                                                "true"
                                                    ? "Hide $0 balances"
                                                    : "Show all balances"}
                                            </FilterButton>
                                        </DropdownTrigger>
                                        <DropdownContent
                                            align="end"
                                            width="auto"
                                        >
                                            <DropdownItem
                                                isSelected={
                                                    excludeZeroBalanceFilter !==
                                                    "true"
                                                }
                                                onClick={() =>
                                                    handleSetExcludeZeroBalanceFilter(
                                                        ""
                                                    )
                                                }
                                            >
                                                Show all balances
                                            </DropdownItem>
                                            <DropdownItem
                                                isSelected={
                                                    excludeZeroBalanceFilter ===
                                                    "true"
                                                }
                                                onClick={() =>
                                                    handleSetExcludeZeroBalanceFilter(
                                                        "true"
                                                    )
                                                }
                                            >
                                                Hide $0 balances
                                            </DropdownItem>
                                        </DropdownContent>
                                    </Dropdown>
                                    <Dropdown>
                                        <DropdownTrigger asChild>
                                            <FilterButton>
                                                {customDateFilter
                                                    ? truncateString(
                                                          makeStringFirstLetterCapital(
                                                              removeEnumUnderscore(
                                                                  customDateFilter
                                                              )
                                                          )
                                                      )
                                                    : "Date range"}
                                            </FilterButton>
                                        </DropdownTrigger>
                                        <DropdownContent
                                            align="start"
                                            width={150}
                                        >
                                            {[
                                                ...AGING_AGE_FILTERS.keys(),
                                                ...Object.values(
                                                    IAppCustomDates
                                                ),
                                            ].map((range) => (
                                                <DropdownItem
                                                    isSelected={
                                                        customDateFilter ===
                                                        range
                                                    }
                                                    key={range}
                                                    onClick={() =>
                                                        handleSetCustomDateFilter(
                                                            range
                                                        )
                                                    }
                                                >
                                                    {removeEnumUnderscore(
                                                        range
                                                    )}
                                                </DropdownItem>
                                            ))}
                                        </DropdownContent>
                                    </Dropdown>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <FilterButton>
                                                Date Of Service
                                            </FilterButton>
                                        </PopoverTrigger>
                                        <PopoverContent align="end">
                                            <div className="mb-1">
                                                <DatePicker
                                                    label="From"
                                                    placeholderText="MM/DD/YYYY"
                                                    selected={
                                                        fromDateFilterToDate
                                                    }
                                                    onChange={
                                                        handleSetFromDateFilter
                                                    }
                                                    maxDate={
                                                        toDateFilterToDate ||
                                                        new Date(Date.now())
                                                    }
                                                    isClearable
                                                />
                                            </div>
                                            <DatePicker
                                                label="To"
                                                placeholderText="MM/DD/YYYY"
                                                selected={toDateFilterToDate}
                                                onChange={handleSetToDateFilter}
                                                className="hover:border-primary"
                                                minDate={fromDateFilterToDate}
                                                isClearable
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                                <div className="flex gap-x-2">
                                    {listLengthIsMoreThanExportLimit && (
                                        <ActionButton
                                            variant="normal"
                                            size="auto"
                                            actionTitle="Export limit reached."
                                        >
                                            <InfoIcon
                                                stroke={APP_COLORS.COLOR_DANGER}
                                            />
                                        </ActionButton>
                                    )}
                                    <Button
                                        onClick={handleExportClick}
                                        disabled={
                                            isExporting ||
                                            data?.data.length === 0 ||
                                            listLengthIsMoreThanExportLimit
                                        }
                                    >
                                        {isExporting
                                            ? "Exporting..."
                                            : "Export"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <TableFiltersDisplay
                            appliedFilters={[
                                {
                                    key: "client_names[]",
                                    values: clientNamesFilter,
                                },
                                {
                                    key: "providers[]",
                                    values: providers?.data
                                        ? providers?.data
                                              .filter((provider) =>
                                                  providersFilter.includes(
                                                      provider.provider_id
                                                  )
                                              )
                                              .map(
                                                  (provider) =>
                                                      provider.provider_name ||
                                                      ""
                                              )
                                        : [],
                                },
                                {
                                    key: "cpt_codes[]",
                                    values: cptCodesFilter,
                                },
                                {
                                    key: "pos_codes[]",
                                    values: posCodesFilter,
                                },
                                {
                                    key: "claim_statuses[]",
                                    values: claimStatusesFilter,
                                },
                                {
                                    key: "bill_statuses[]",
                                    values: billStatusesFilter,
                                },
                                {
                                    key: "insurance_provider_ids[]",
                                    values:
                                        filteredInsuranceProviderNames || [],
                                },
                                {
                                    key: "remark_codes[]",
                                    values: remarkCodesFilter.map((code) =>
                                        code.toUpperCase()
                                    ),
                                },
                                {
                                    key: "exclude_zero_balance",
                                    label: "Claim balance",
                                    values:
                                        excludeZeroBalanceFilter === "true"
                                            ? "Hide $0 balances"
                                            : "",
                                },
                                {
                                    key: "from_date",
                                    values: fromDateFilter || "",
                                },
                                {
                                    key: "to_date",
                                    values: toDateFilter || "",
                                },
                                {
                                    key: "custom_date",
                                    values: customDateFilter || "",
                                },
                            ]}
                            wrapperClass="mt-[--spacing-items]"
                            localStorageKey="agingreports_filters"
                        />
                    </Card>
                </div>
                {isLoading && <Loader />}
                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display aging report list at this time please try again later`
                        }
                    />
                )}
                {isSuccess && data.data.length === 0 && (
                    <ListState
                        stateHelperText="Aging report list will appear here once available"
                        isError={false}
                        emptyMessage="No aging report list to show"
                    />
                )}
                {isSuccess && data.data.length > 0 && (
                    <Table
                        columns={columns}
                        data={data.data}
                        setSortConfig={setSortConfig}
                        sortConfig={sortConfig}
                        hasPagination
                        sortableColumns={sortableColumns}
                        isHeaderFixed
                        handleRowClick={handleRowClick}
                        showSizeFilter
                        isColumnVisible={columnVisibility}
                        showColumnVisibilityFilter
                        columnVisibilityDropdownItems={
                            <>
                                {table.getAllLeafColumns().map((column) => {
                                    return (
                                        <DropdownItem
                                            key={column.id}
                                            onClick={() =>
                                                column.toggleVisibility()
                                            }
                                            className="*:!uppercase"
                                            isMulti
                                            itemValue={column.id}
                                            values={table
                                                .getVisibleLeafColumns()
                                                .map((col) => col.id)}
                                        >
                                            {column?.columnDef?.header?.toString() ||
                                                column.id?.replaceAll("_", " ")}
                                        </DropdownItem>
                                    );
                                })}
                            </>
                        }
                        pagination={
                            <Pagination
                                totalCount={data.total_count}
                                totalPages={data.total_pages}
                                currentPage={pageFilter}
                                onPageChange={handleSetPageFilter}
                                limit={limitFilter}
                                onLimitChange={handleSetLimitFilter}
                            />
                        }
                    />
                )}
            </div>
        </>
    );
}
